import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../Toast';
import { EmptyState } from '../EmptyState';
import { Button, Card, Badge, Text, Cluster, Stack, Modal, Field, Input } from '../ui';
import { formatCents } from '../../lib/billing-admin-queries';
import {
  chargeAuctionWinner,
  refundAuctionCharge,
  fetchPostagePreview,
  buyAuctionLabel,
  relistAuction,
  fulfillmentStage,
  stageCounts,
  type SaleroomChargeRow,
  type FulfillmentStage,
} from '../../lib/saleroom-admin-queries';

/**
 * Orders: the Saleroom fulfilment workspace. One row per ended/sold lot, moving
 * through the pipeline (needs action -> in progress -> ready to ship -> shipped
 * -> delivered). Winners are charged automatically on close, so the team's main
 * job here is "Buy label" on the Ready-to-ship queue; the exceptions
 * (declines / SCA / no card) surface under "Needs action".
 *
 * Rows are owned by Admin so the nav can badge outstanding work; this renders
 * them, filters by stage, and calls onRefresh after any action.
 */

const STAGE_META: Record<FulfillmentStage, { label: string; tone: 'warning' | 'neutral' | 'accent' | 'ink' | 'success' }> = {
  needs_action:  { label: 'Needs action',  tone: 'warning' },
  in_progress:   { label: 'In progress',   tone: 'neutral' },
  ready_to_ship: { label: 'Ready to ship', tone: 'accent'  },
  shipped:       { label: 'Shipped',       tone: 'ink'     },
  delivered:     { label: 'Delivered',     tone: 'success' },
  expired:       { label: 'Expired',       tone: 'warning' },
  refunded:      { label: 'Refunded',      tone: 'neutral' },
};

const FILTERS: Array<{ id: FulfillmentStage | 'all'; label: string }> = [
  { id: 'needs_action',  label: 'Needs action' },
  { id: 'ready_to_ship', label: 'Ready to ship' },
  { id: 'shipped',       label: 'Shipped' },
  { id: 'delivered',     label: 'Delivered' },
  { id: 'in_progress',   label: 'In progress' },
  { id: 'expired',       label: 'Expired' },
  { id: 'refunded',      label: 'Refunded' },
  { id: 'all',           label: 'All' },
];

/** Format a Date for a datetime-local input (local time, no seconds) - matches
 *  the team's lot-listing control. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Compact "buyer has X left" / "window lapsed" label from a deadline. */
function deadlineLabel(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  if (ms <= 0) return 'Window lapsed';
  const hours = ms / 3_600_000;
  if (hours >= 36) return `Buyer has ${Math.round(hours / 24)} days to pay`;
  if (hours >= 2) return `Buyer has ~${Math.round(hours)}h to pay`;
  return 'Buyer has <2h to pay';
}

export function AdminOrders({
  rows, loading, loaded, error, onRefresh,
}: {
  rows: SaleroomChargeRow[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  onRefresh: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [busyPost, setBusyPost] = useState<string | null>(null);
  // Relist modal: the lot being relisted + its chosen close date/time.
  const [relistRow, setRelistRow] = useState<SaleroomChargeRow | null>(null);
  const [relistEndsAt, setRelistEndsAt] = useState('');
  const [relistBusy, setRelistBusy] = useState(false);
  const counts = useMemo(() => stageCounts(rows), [rows]);

  // Default to the first stage that actually has work, else "all".
  const [filter, setFilter] = useState<FulfillmentStage | 'all'>(() => {
    const c = stageCounts(rows);
    if (c.needs_action || c.expired) return 'needs_action';
    if (c.ready_to_ship) return 'ready_to_ship';
    return 'all';
  });

  async function handleCharge(row: SaleroomChargeRow) {
    setBusyPost(row.post_id);
    let confirmText: string;
    try {
      const p = await fetchPostagePreview(row.post_id);
      const postageLine =
        p.postage_source === 'live_rate'
          ? `+ ${formatCents(p.postage_cents)} postage (live rate${p.postage_service ? ` - ${p.postage_service}` : ''})`
          : p.postage_source === 'flat'
          ? `+ ${formatCents(p.postage_cents)} flat postage`
          : '+ no postage set';
      confirmText =
        `Charge ${row.winner_name || 'the winner'} ${formatCents(p.total_cents)} for ` +
        `LOT ${row.lot_number} - ${row.lot_name}?\n\n` +
        `Hammer ${formatCents(p.hammer_cents)} ${postageLine}` +
        `${p.destination ? `\nShips to: ${p.destination}` : '\nNo delivery address on file.'}\n\n` +
        `This charges their saved card off-session. It cannot be undone except by a refund.`;
    } catch (err: any) {
      setBusyPost(null);
      toast.error(err?.message ?? 'Could not fetch the postage quote - charge not started.');
      return;
    }
    if (!window.confirm(confirmText)) { setBusyPost(null); return; }
    try {
      const res = await chargeAuctionWinner(row.post_id);
      if (res.ok && res.status === 'succeeded') toast.success(`Charged: ${formatCents(row.amount_cents)} captured.`);
      else if (res.status === 'requires_action') toast.success('Charge started: the buyer must complete a security check.');
      else if (res.status === 'processing') toast.success('Charge processing: status updates shortly.');
      else toast.error(`Charge ${res.status}${res.error ? `: ${res.error}` : ''}.`);
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Charge failed.');
    } finally {
      setBusyPost(null);
    }
  }

  async function handleBuyLabel(row: SaleroomChargeRow) {
    setBusyPost(row.post_id);
    try {
      const res = await buyAuctionLabel(row.post_id);
      toast.success(res.tracking_number ? `Label bought - tracking ${res.tracking_number}.` : 'Label bought.');
      window.open(res.label_url, '_blank', 'noopener');
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Label purchase failed.');
    } finally {
      setBusyPost(null);
    }
  }

  function openRelist(row: SaleroomChargeRow) {
    // Default to closing in 3 days, matching the team's typical lot run.
    setRelistEndsAt(toLocalInput(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)));
    setRelistRow(row);
  }

  async function confirmRelist() {
    if (!relistRow || !relistEndsAt) return;
    const endsAt = new Date(relistEndsAt);
    if (isNaN(endsAt.getTime()) || endsAt.getTime() <= Date.now()) {
      toast.error('Pick a close date in the future.');
      return;
    }
    setRelistBusy(true);
    try {
      await relistAuction(relistRow.post_id, endsAt.toISOString());
      toast.success('Relisted - the lot is live again.');
      setRelistRow(null);
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message === 'already_paid'
        ? 'That lot was already paid - it can’t be relisted.'
        : err?.message ?? 'Relist failed.');
    } finally {
      setRelistBusy(false);
    }
  }

  async function handleRefund(row: SaleroomChargeRow) {
    if (!window.confirm(
      `Refund ${formatCents(row.amount_cents)} to ${row.winner_name || 'the winner'} for LOT ${row.lot_number}? This returns the full amount.`,
    )) return;
    setBusyPost(row.post_id);
    try {
      await refundAuctionCharge(row.post_id);
      toast.success('Refunded.');
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Refund failed.');
    } finally {
      setBusyPost(null);
    }
  }

  function renderAction(row: SaleroomChargeRow, stage: FulfillmentStage) {
    const busy = busyPost === row.post_id;
    if (stage === 'needs_action') {
      if (!row.winner_has_card) {
        return <Text role="caption" muted title="Winner has no card on file">No card on file</Text>;
      }
      const retry = row.charge_status === 'failed' || row.charge_status === 'canceled';
      return (
        <Button type="button" variant="primary" size="sm" loading={busy} onClick={() => handleCharge(row)}>
          {retry ? 'Retry charge' : 'Charge'}
        </Button>
      );
    }
    if (stage === 'ready_to_ship') {
      return (
        <Cluster gap="tight" wrap>
          <Button type="button" variant="accent" size="sm" loading={busy} onClick={() => handleBuyLabel(row)}>
            Buy label
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => handleRefund(row)} disabled={busy}>
            Refund
          </Button>
        </Cluster>
      );
    }
    if (stage === 'shipped' || stage === 'delivered') {
      return (
        <Cluster gap="tight" wrap>
          {row.label_url && (
            <a className="btn btn--ghost btn--sm" href={row.label_url} target="_blank" rel="noopener noreferrer">
              Label
            </a>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={() => handleRefund(row)} disabled={busy}>
            Refund
          </Button>
        </Cluster>
      );
    }
    if (stage === 'in_progress') {
      return (
        <Text role="caption" muted>
          {row.charge_status === 'requires_action' ? 'Waiting on buyer security check' : 'Processing payment'}
        </Text>
      );
    }
    if (stage === 'expired') {
      // Forfeited: the buyer's window lapsed. Nothing was captured, so there's
      // no refund - the team decides (relist, offer an underbidder, or pull)
      // using the lot tools. We can still re-charge if they sort their card.
      return (
        <Cluster gap="tight" wrap>
          <Button type="button" variant="primary" size="sm" onClick={() => openRelist(row)}>
            Relist
          </Button>
          {row.winner_has_card && (
            <Button type="button" variant="soft" size="sm" loading={busy} onClick={() => handleCharge(row)}>
              Charge anyway
            </Button>
          )}
          {row.post_slug && (
            <a className="btn btn--ghost btn--sm" href={`/p/${row.post_slug}`} target="_blank" rel="noopener noreferrer">
              View lot
            </a>
          )}
        </Cluster>
      );
    }
    return null;
  }

  if (!loaded && loading) {
    return <Text role="small" muted>Loading orders…</Text>;
  }

  // Expired (forfeited) orders are surfaced under "Needs action" too, so the
  // team's main work queue doesn't miss them - they keep their own chip as well.
  const visible = filter === 'all'
    ? rows
    : filter === 'needs_action'
      ? rows.filter((r) => { const s = fulfillmentStage(r); return s === 'needs_action' || s === 'expired'; })
      : rows.filter((r) => fulfillmentStage(r) === filter);

  return (
    <Stack gap="group">
      <Cluster justify="between" align="center" wrap gap="group">
        <div>
          <p className="kicker">Orders</p>
          <Text role="small" muted>
            Winners are charged automatically on close. Your queue is <strong>Ready to ship</strong>:
            buy the label and hand it to the carrier. Tracking then updates itself.
          </Text>
        </div>
        <Button type="button" variant="soft" size="sm" onClick={() => onRefresh()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Cluster>

      {error && (
        <Card surface="bone" pad="sm"><Text role="small" className="text-danger">{error}</Text></Card>
      )}

      {rows.length === 0 ? (
        <EmptyState
          tone="compact" align="start"
          eyebrow="Nothing to fulfil"
          headline="No sold lots yet."
          body="Once a lot closes with a winner (or sells via Buy Now), it shows here."
        />
      ) : (
        <>
          {/* Stage filter chips */}
          <Cluster gap="tight" wrap role="tablist" aria-label="Order stage filter">
            {FILTERS.map((f) => {
              const count = f.id === 'all' ? rows.length
                : f.id === 'needs_action' ? counts.needs_action + counts.expired
                : counts[f.id];
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(f.id)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ' +
                    (active
                      ? 'border-ink bg-ink text-surface'
                      : 'border-border bg-surface text-text-muted hover:border-text-muted')
                  }
                >
                  {f.label}
                  <span className={active ? 'opacity-80' : 'text-text-faint'}>{count}</span>
                </button>
              );
            })}
          </Cluster>

          {visible.length === 0 ? (
            <EmptyState
              tone="compact" align="start"
              eyebrow="Clear"
              headline="Nothing in this stage."
              body="No orders are currently here."
            />
          ) : (
            <Stack gap="tight">
              {visible.map((row) => {
                const stage = fulfillmentStage(row);
                const meta = STAGE_META[stage];
                return (
                  <Card key={row.post_id} bordered pad="sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      {/* Lot + buyer */}
                      <div className="min-w-0">
                        <Cluster gap="tight" align="center" wrap>
                          <Text role="label" as="span">LOT {row.lot_number}</Text>
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                          {row.charge_origin === 'buy_now' && <Badge tone="neutral">Buy Now</Badge>}
                        </Cluster>
                        <Text role="small" muted>
                          {row.post_slug ? <Link to={`/p/${row.post_slug}`}>{row.lot_name}</Link> : row.lot_name}
                          {' · '}
                          {row.winner_name || 'winner'}{row.winner_handle ? ` (@${row.winner_handle})` : ''}
                        </Text>
                        {row.last_error && stage === 'needs_action' && (
                          <Text role="caption" className="text-danger" title={row.last_error}>
                            {row.last_error.slice(0, 64)}
                          </Text>
                        )}
                        {(stage === 'needs_action' || stage === 'in_progress') && deadlineLabel(row.action_deadline) && (
                          <Text role="caption" muted>{deadlineLabel(row.action_deadline)}</Text>
                        )}
                        {row.tracking_number && (
                          <Text role="caption" muted>
                            {row.tracking_url
                              ? <a href={row.tracking_url} target="_blank" rel="noopener noreferrer">{row.tracking_number}</a>
                              : row.tracking_number}
                            {row.tracking_status ? ` · ${row.tracking_status.toLowerCase().replace(/_/g, ' ')}` : ''}
                          </Text>
                        )}
                      </div>

                      {/* Amount + action */}
                      <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-1">
                        <div className="text-right">
                          <Text role="label" as="span">{formatCents(row.amount_cents)}</Text>
                          {row.postage_cents != null && row.postage_cents > 0 && (
                            <Text role="caption" muted>
                              incl. {formatCents(row.postage_cents)} postage
                              {row.postage_source === 'live_rate' && row.postage_service ? ` (${row.postage_service})` : ''}
                            </Text>
                          )}
                        </div>
                        <div className="shrink-0">{renderAction(row, stage)}</div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </Stack>
          )}
        </>
      )}

      <Modal
        open={relistRow !== null}
        onClose={() => { if (!relistBusy) setRelistRow(null); }}
        title="Relist lot"
        size="sm"
      >
        {relistRow && (
          <Stack gap="group">
            <Text role="small" muted>
              Relist <strong>LOT {relistRow.lot_number} - {relistRow.lot_name}</strong> as a
              fresh auction. This clears the old bids and charge history and reopens the lot
              at its previous sale price as the opening bid.
            </Text>
            <Field label="Closes at" htmlFor="relist-ends">
              <Input
                id="relist-ends"
                type="datetime-local"
                value={relistEndsAt}
                onChange={(e) => setRelistEndsAt(e.target.value)}
                disabled={relistBusy}
                required
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="md" onClick={() => setRelistRow(null)} disabled={relistBusy}>
                Cancel
              </Button>
              <Button type="button" variant="primary" size="md" onClick={confirmRelist} loading={relistBusy}>
                Relist
              </Button>
            </div>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
